// SiegerehrungDisplayPage.jsx — Vollbild fuer zweiten Bildschirm / Beamer.
//
// Seit 03.10.2026 (Aufgabe 149) liest der Bildschirm unter
// /siegerehrung-display/:turnierId vom Server (GET /api/siegerehrung/turnier/
// :id/anzeige, alle 2 s) — er laeuft damit auf JEDEM Geraet: Beamer-Laptop,
// Hallen-TV, Tablet. Vorher ging er nur ueber localStorage im selben Browser
// wie die Steuerung. Der lokale Weg (storage-Event + BroadcastChannel) bleibt
// als Sofort-Kanal, wenn beide Fenster auf demselben Rechner laufen.
// Ohne Vollbild wird trotzdem angezeigt (Fernseher ohne Vollbild-Funktion
// blieben sonst auf der Einrichtungsseite haengen); die Einrichtung ist nur
// noch eine Leiste am unteren Rand.
//
// Gestaltung seit 20.10.2026: Turniertag-Bild nach dem abgenommenen Muster
// (components/leinwand/SiegerBuehne, buehne.jpg). Die Einrichtungsleiste
// steht weiter in SiegerehrungDisplayPage.css.
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import './SiegerehrungDisplayPage.css';
// Bühne, Podest und Ablauf (Muster „Siegerehrung“, 20.10.2026)
import SiegerBuehne from '../components/leinwand/SiegerBuehne';
import '../components/leinwand/SiegerBuehne.css';

const CHANNEL = 'tda-siegerehrung';

const NEU_LADEN_MS = 2000;

export default function SiegerehrungDisplayPage() {
  const params = useParams();
  const turnierId = params.turnierId || (() => {
    try { return localStorage.getItem('tda-selected-turnier-id'); } catch { return null; }
  })();
  // Hallenschlüssel (k=, Leitstelle): nur damit kommen Fotos (21.10.2026)
  const k = new URLSearchParams(window.location.search).get('k') || '';
  const halle = useMemo(() => (turnierId && k ? { turnierId, k } : null), [turnierId, k]);
  const [data, setData]           = useState(null);
  const [turnierName, setTurnierName] = useState('');
  const letzteVersion = useRef(null);
  // Einrichtungsleiste: nach 12 s ohne Maus/Touch weg, damit sie am Beamer
  // nicht über der Anzeige stehen bleibt; jede Bewegung holt sie zurück.
  const [leisteSichtbar, setLeisteSichtbar] = useState(true);
  useEffect(() => {
    let timer = setTimeout(() => setLeisteSichtbar(false), 12000);
    const zeigen = () => {
      setLeisteSichtbar(true);
      clearTimeout(timer);
      timer = setTimeout(() => setLeisteSichtbar(false), 12000);
    };
    window.addEventListener('mousemove', zeigen);
    window.addEventListener('touchstart', zeigen);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousemove', zeigen);
      window.removeEventListener('touchstart', zeigen);
    };
  }, []);
  const letzterSchluessel = useRef('');
  const [animKey, setAnimKey]     = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Overflow hidden
  useEffect(() => {
    const prev = {
      htmlOverflow: document.documentElement.style.overflow,
      bodyOverflow: document.body.style.overflow,
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.body.style.overflow = prev.bodyOverflow;
    };
  }, []);

  // Auto-Vollbild beim Laden (klappt wenn via Popup aus User-Geste geöffnet)
  useEffect(() => {
    const timer = setTimeout(() => {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Fullscreen-Status tracken
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }
  };

  // Chat-Widget und externe Elemente verstecken
  useEffect(() => {
    const hide = () => {
      document.body.querySelectorAll('body > *:not(#root)').forEach(el => {
        if (!['SCRIPT','STYLE','LINK','META'].includes(el.tagName)) {
          el.style.setProperty('display', 'none', 'important');
        }
      });
    };
    hide();
    const obs = new MutationObserver(hide);
    obs.observe(document.body, { childList: true });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const LS_KEY = 'tda-siegerehrung-state';

    const applyState = (raw) => {
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        // Paket eines anderen Turniers (zweites Dashboard im selben Browser,
        // alter Stand im Speicher): nicht zeigen (21.10.2026)
        if (parsed?.turnier_id && turnierId && String(parsed.turnier_id) !== String(turnierId)) return;
        if (parsed?.type === 'show' && parsed.kategorie) {
          const schluessel = `${parsed.kategorie.typ}-${parsed.kategorie.kategorie_id}`;
          setData({ kategorie: parsed.kategorie, idx: parsed.idx, total: parsed.total });
          if (schluessel !== letzterSchluessel.current) setAnimKey(k => k + 1);
          letzterSchluessel.current = schluessel;
        } else {
          setData(null);
          letzterSchluessel.current = '';
        }
      } catch (e) {}
    };

    // 1) Sofort lesen beim Öffnen
    applyState(localStorage.getItem(LS_KEY));

    // 2) storage-Event: feuert in ALLEN anderen Fenstern wenn localStorage sich ändert
    //    (zuverlässiger als BroadcastChannel in Safari)
    const onStorage = (e) => {
      if (e.key === LS_KEY) applyState(e.newValue);
    };
    window.addEventListener('storage', onStorage);

    // 3) BroadcastChannel als zusätzlicher Kanal (für gleichen Tab)
    let ch = null;
    try {
      ch = new BroadcastChannel(CHANNEL);
      ch.onmessage = (e) => applyState(e.data);
    } catch (e) {}

    // 4) Server: der Weg fuer jedes andere Geraet. Nur bei neuer Version
    //    uebernehmen, sonst wuerde der Server einen gerade lokal gezeigten
    //    Stand mit einem 2 s alten ueberschreiben.
    let aus = false;
    const holen = async () => {
      if (!turnierId) return;
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`/api/siegerehrung/turnier/${turnierId}/anzeige${k ? `?k=${encodeURIComponent(k)}` : ''}`, {
          cache: 'no-store', headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok) return;
        const d = await r.json();
        if (aus) return;
        if (d.turnier_name) setTurnierName(d.turnier_name);
        if (d.version !== letzteVersion.current) {
          letzteVersion.current = d.version;
          applyState(d);
        }
      } catch (e) { /* naechster Versuch in 2 s */ }
    };
    holen();
    const iv = setInterval(holen, NEU_LADEN_MS);

    return () => {
      aus = true;
      clearInterval(iv);
      window.removeEventListener('storage', onStorage);
      if (ch) ch.close();
    };
  }, [turnierId, k]);

  return (
    <>
      {!data
        ? <StandbyScreen onFullscreen={toggleFullscreen} turnierName={turnierName} />
        : <PodiumDisplay key={animKey} data={data} onFullscreen={toggleFullscreen} halle={halle} />}
      {!isFullscreen && leisteSichtbar && <SetupLeiste onFullscreen={toggleFullscreen} turnierId={turnierId} />}
    </>
  );
}

// ── Einrichtungsleiste (nur ohne Vollbild) ──────────────────────────
function SetupLeiste({ onFullscreen, turnierId }) {
  const [copied, setCopied] = useState(false);
  // Mit Schlüssel (k=) aus der eigenen Adresse, sonst zeigt das andere Gerät keine Fotos
  const displayUrl = `${window.location.origin}/siegerehrung-display${turnierId ? `/${turnierId}` : ''}${window.location.search}`;
  const copyUrl = () => {
    navigator.clipboard?.writeText(displayUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => {});
  };
  return (
    <div className="sdp-leiste">
      <button className="sdp-leiste-btn" onClick={onFullscreen}>Vollbild starten</button>
      <span className="sdp-leiste-text">
        {turnierId
          ? <>Auf einem anderen Gerät (Beamer, Hallen-TV) diese Adresse öffnen: <strong>{displayUrl}</strong></>
          : 'Kein Turnier gewählt: Bildschirm aus dem Dashboard heraus öffnen.'}
      </span>
      {turnierId && <button className="sdp-leiste-btn leise" onClick={copyUrl}>{copied ? 'Kopiert' : 'Adresse kopieren'}</button>}
    </div>
  );
}

// ── Standby und Podest: Turniertag-Bild (20.10.2026, Muster „Siegerehrung“) ──
function StandbyScreen({ onFullscreen, turnierName }) {
  return (
    <div onClick={onFullscreen} title="Klicken für Vollbild">
      <div className="sb-flaeche">
        <div className="sb" style={{ backgroundImage: `url("${process.env.PUBLIC_URL || ''}/turniertag/buehne.jpg")` }}>
          <div className="sb-kopf"><div className="sb-titel">Siegerehrung</div></div>
          <div className="sb-turnier">{turnierName || 'Tiger & Dragon Association'}</div>
          <div className="sb-bereit">Bereit</div>
        </div>
      </div>
    </div>
  );
}

function PodiumDisplay({ data, onFullscreen, halle }) {
  return (
    <div onClick={onFullscreen}>
      <SiegerBuehne kategorie={data.kategorie} idx={data.idx} total={data.total} halle={halle} />
    </div>
  );
}
